/**
 * Trip Timeline PDF Export
 * Generates a styled PDF with the day-by-day trip story.
 */

const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d + 'T00:00:00');
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
};

export const downloadTimelinePdf = (timeline) => {
  const { trip_input, days, summary, review, budget } = timeline;
  const city = trip_input?.destination_cities?.length > 1
    ? trip_input.destination_cities.join(' > ')
    : trip_input?.destination_city || 'Trip';
  const country = trip_input?.destination_country || '';
  const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // Build day cards HTML
  let daysHtml = '';
  days.forEach((day) => {
    let activitiesHtml = '';
    day.activities.forEach((act) => {
      activitiesHtml += `
        <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:6px;">
          <div style="width:6px;height:6px;border-radius:50%;background:#d4a843;margin-top:6px;flex-shrink:0;"></div>
          <div style="flex:1;">
            <div style="display:flex;align-items:center;gap:6px;">
              ${act.time ? `<span style="color:#6b7280;font-size:11px;font-family:monospace;">${act.time}</span>` : ''}
              <span style="font-weight:600;font-size:13px;color:#1f2937;">${act.name}</span>
            </div>
            ${act.description ? `<p style="color:#6b7280;font-size:11px;margin:2px 0 0 0;">${act.description}</p>` : ''}
          </div>
        </div>`;
    });

    let expensesHtml = '';
    if (day.expenses?.length > 0) {
      const expTags = day.expenses.map(exp =>
        `<span style="background:#fef3c7;color:#92400e;padding:2px 8px;border-radius:12px;font-size:11px;display:inline-block;margin:2px;">${exp.category}: ${exp.currency} ${exp.amount.toFixed(0)} - ${exp.description}</span>`
      ).join('');
      expensesHtml = `<div style="margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;">${expTags}</div>`;
    }

    let storyNoteHtml = '';
    if (day.story_note) {
      storyNoteHtml = `
        <div style="background:#fffbeb;border-left:3px solid #d4a843;padding:8px 12px;border-radius:0 8px 8px 0;margin-bottom:10px;">
          <p style="color:#4b5563;font-size:12px;font-style:italic;margin:0;line-height:1.5;">${day.story_note}</p>
        </div>`;
    }

    daysHtml += `
      <div style="page-break-inside:avoid;margin-bottom:16px;">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px;">
          <div style="width:28px;height:28px;border-radius:50%;background:#d4a843;color:#0B3C5D;display:flex;align-items:center;justify-content:center;font-weight:700;font-size:12px;flex-shrink:0;">${day.day_number}</div>
          <div style="flex:1;">
            <div style="font-weight:600;font-size:14px;color:#0B3C5D;">${day.title || 'Day ' + day.day_number}</div>
            <div style="font-size:11px;color:#6b7280;">${formatDate(day.date)}${day.city ? ' | ' + day.city : ''}</div>
          </div>
          ${day.day_expense_total > 0 ? `<div style="background:#fef3c7;color:#92400e;padding:3px 10px;border-radius:12px;font-size:11px;font-weight:600;">$${day.day_expense_total.toFixed(0)}</div>` : ''}
        </div>
        <div style="background:white;border:1px solid #e5e7eb;border-radius:10px;padding:14px;margin-left:38px;">
          ${storyNoteHtml}
          ${activitiesHtml}
          ${expensesHtml}
        </div>
      </div>`;
  });

  // Review section
  let reviewHtml = '';
  if (review) {
    const stars = Array.from({ length: 5 }).map((_, i) =>
      `<span style="color:${i < review.rating ? '#d4a843' : '#d1d5db'};font-size:16px;">&starf;</span>`
    ).join('');
    reviewHtml = `
      <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:10px;padding:16px;margin-top:20px;page-break-inside:avoid;">
        <div style="margin-bottom:6px;">${stars}</div>
        <p style="color:#4b5563;font-size:13px;font-style:italic;margin:0;">"${review.text}"</p>
      </div>`;
  }

  const html = `
    <html>
    <head>
      <style>
        @page { margin: 36px; size: A4; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1f2937; line-height: 1.5; background: #f9fafb; }
        .header { background: linear-gradient(135deg, #0B3C5D, #1a6b8a); color: white; padding: 28px 30px; border-radius: 12px; margin-bottom: 20px; }
        .header h1 { font-size: 26px; margin: 0 0 4px 0; }
        .header p { margin: 2px 0; opacity: 0.85; font-size: 13px; }
        .stats { display: flex; gap: 10px; margin-bottom: 20px; }
        .stat { flex: 1; background: white; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; text-align: center; }
        .stat .label { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
        .stat .val { font-size: 20px; font-weight: 700; color: #0B3C5D; margin-top: 2px; }
        .footer { margin-top: 24px; text-align: center; font-size: 10px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 10px; }
      </style>
    </head>
    <body>
      <div class="header">
        <p style="font-size:11px;text-transform:uppercase;letter-spacing:2px;opacity:0.7;margin-bottom:4px;">Trip Timeline</p>
        <h1>${city}</h1>
        <p>${country}</p>
        <p>${trip_input?.start_date ? formatDate(trip_input.start_date) + ' - ' + formatDate(trip_input.end_date) : ''} | ${trip_input?.adults || 1} adult(s)</p>
      </div>

      <div class="stats">
        <div class="stat">
          <div class="label">Days</div>
          <div class="val">${summary?.total_days || 0}</div>
        </div>
        <div class="stat">
          <div class="label">Cities</div>
          <div class="val">${summary?.cities_visited?.length || 0}</div>
        </div>
        <div class="stat">
          <div class="label">Photos</div>
          <div class="val">${summary?.total_photos || 0}</div>
        </div>
        <div class="stat">
          <div class="label">Spent</div>
          <div class="val">$${summary?.total_spent?.toFixed(0) || '0'}</div>
        </div>
      </div>

      ${daysHtml}
      ${reviewHtml}

      <div class="footer">
        <p>Generated on ${now} | Triple A Travel Solutions | tripleabooking.com</p>
      </div>
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${city.replace(/[^a-zA-Z0-9]/g, '-')}-${country.replace(/[^a-zA-Z0-9]/g, '-')}-Trip-Timeline.html`;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
};

export const getTimelinePdfFileName = (tripInput) => {
  const city = tripInput?.destination_cities?.length > 1
    ? tripInput.destination_cities.join('-')
    : tripInput?.destination_city || 'Trip';
  const country = tripInput?.destination_country || '';
  return `${city} ${country} Trip Timeline`.trim();
};
